import { useEffect, useState } from 'react'
import { apiPaths } from '../../../../../service/apiPaths';
import ApiService from '../../../../../service/ApiService';
import { Roles } from '../../../../../util/constant';

export interface CampaignItem {
    _id: string; // mapped from campaignId for consistency across app
    campaignId: string;
    campaignName: string;
    leadCount: number; // totalLeads from API
    scheduled: number; // scheduledCount from API
    queryCount: number; // queryCount from API
}

const SELECTED_KEY = 'selectedCampaignId';

const useCampaignData = () => {
    const [campaigns, setCampaigns] = useState<CampaignItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [creating, setCreating] = useState<boolean>(false);
    const [newCampaignName, setNewCampaignName] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
        (typeof window !== 'undefined' && localStorage.getItem(SELECTED_KEY)) || null
    );

    const fetchCampaigns = async () => {
        setLoading(true);
        setError(null);
        try {
            // 1) Fetch campaigns from Instantly (external source)
            const instantlyResp = await ApiService({
                method: 'GET',
                endpoint: apiPaths.instantlyCampaigns,
            });

            // Normalize instantly campaigns from possible response shapes
            const instantlyListRaw: any[] = Array.isArray(instantlyResp)
                ? instantlyResp
                : Array.isArray(instantlyResp?.campaigns)
                ? instantlyResp.campaigns
                : Array.isArray(instantlyResp?.data)
                ? instantlyResp.data
                : Array.isArray(instantlyResp?.items)
                ? instantlyResp.items
                : [];

            const normalizeInstantlyName = (c: any) =>
                (c?.name || c?.campaignName || c?.title || '').trim();
            const normalizeInstantlyId = (c: any) => c?.id || c?._id || c?.campaignId || '';

            // Map lowercased name -> original name to preserve casing
            const instantlyNameMap = new Map<string, string>();
            for (const c of instantlyListRaw) {
                const original = normalizeInstantlyName(c);
                if (original) instantlyNameMap.set(original.toLowerCase(), original);
            }
            const instantlyNamesSet = new Set(instantlyNameMap.keys());

            // 2) Fetch our existing campaign masters (to know what to create if missing)
            const localCampaignsResp = await ApiService({
                method: 'GET',
                endpoint: apiPaths.campaignDataGetAll,
            });

            const localMasters: any[] = Array.isArray(localCampaignsResp)
                ? localCampaignsResp
                : Array.isArray(localCampaignsResp?.data)
                ? localCampaignsResp.data
                : Array.isArray(localCampaignsResp?.items)
                ? localCampaignsResp.items
                : Array.isArray(localCampaignsResp?.campaigns)
                ? localCampaignsResp.campaigns
                : [];

            const localNamesSet = new Set(
                (localMasters || [])
                    .map((c: any) => (c?.campaignName || c?.name || '').trim())
                    .filter((n: string) => n)
                    .map((n: string) => n.toLowerCase())
            );

            // 3) Create missing campaigns locally based on Instantly
            const namesToCreate: string[] = Array.from(instantlyNamesSet).filter(
                (n) => !localNamesSet.has(n)
            );

            if (namesToCreate.length > 0) {
                await Promise.all(
                    namesToCreate.map((lowerName) =>
                        ApiService({
                            method: 'POST',
                            endpoint: apiPaths.campaignDataCreate,
                            data: { campaignName: instantlyNameMap.get(lowerName) || lowerName },
                        }).catch(() => null)
                    )
                );
            }

            // 4) Fetch counts after ensuring masters exist
            const response = await ApiService({
                method: 'GET',
                endpoint: apiPaths.campaignGetAll,
            });

            const counts: any[] = Array.isArray(response?.counts) ? response.counts : [];
            let list: CampaignItem[] = counts.map((c: any) => ({
                _id: c?.campaignId || '',
                campaignId: c?.campaignId || '',
                campaignName: c?.campaignName || '-',
                leadCount: typeof c?.totalLeads === 'number' ? c.totalLeads : 0,
                scheduled: typeof c?.scheduledCount === 'number' ? c.scheduledCount : 0,
                queryCount: typeof c?.queryCount === 'number' ? c.queryCount : 0,
            }));

            // 5) Filter to show only campaigns that match Instantly campaigns
            list = list.filter((item) =>
                item?.campaignName ? instantlyNamesSet.has(item.campaignName.toLowerCase()) : false
            );

            // 6) Include newly created campaigns (not present in counts) with zero values
            const existingNames = new Set(list.map((i) => (i.campaignName || '').toLowerCase()));
            const zeroEntries: CampaignItem[] = Array.from(instantlyNamesSet)
                .filter((n) => !existingNames.has(n))
                .map((lowerName) => ({
                    _id: '',
                    campaignId: '',
                    campaignName: instantlyNameMap.get(lowerName) || lowerName,
                    leadCount: 0,
                    scheduled: 0,
                    queryCount: 0,
                }));

            // Combine and de-duplicate by campaign name (case-insensitive),
            // preferring entries with higher counts or with a real id
            const combined = [...list, ...zeroEntries];
            const dedupedMap = new Map<string, CampaignItem>();
            for (const item of combined) {
                const key = (item?.campaignName || '').trim().toLowerCase();
                if (!key) continue;
                const existing = dedupedMap.get(key);
                if (!existing) {
                    dedupedMap.set(key, item);
                } else {
                    const existingScore = (existing.leadCount || 0) + (existing.scheduled || 0) + (existing.queryCount || 0);
                    const itemScore = (item.leadCount || 0) + (item.scheduled || 0) + (item.queryCount || 0);
                    const preferItem = itemScore > existingScore || (!existing._id && !!item._id);
                    if (preferItem) dedupedMap.set(key, item);
                }
            }
            const finalList = Array.from(dedupedMap.values());

            setCampaigns(finalList);
            setTotalItems(finalList.length);
            setPage(1);
            setTotalPages(1);
        } catch (err: any) {
            setError(err?.message || 'Failed to fetch campaigns');
        } finally {
            setLoading(false);
        }
    };

    const createCampaign = async () => {
        if (!newCampaignName.trim()) return;
        setCreating(true);
        setError(null);
        try {
            const payload = { campaignName: newCampaignName.trim() };
            const created = await ApiService({
                method: 'POST',
                endpoint: apiPaths.campaignDataCreate,
                data: payload,
            });
            setNewCampaignName('');
            // Optimistically add or refetch
            if (created && created._id) {
                const createdItem: CampaignItem = {
                    _id: created._id,
                    campaignId: created._id,
                    campaignName: created.campaignName || newCampaignName.trim(),
                    leadCount: created.leadCount ?? 0,
                    scheduled: created.scheduled ?? 0,
                    queryCount: created.queryCount ?? 0,
                };
                setCampaigns(prev => [createdItem, ...prev]);
                setSelectedCampaignId(created._id);
                if (typeof window !== 'undefined') localStorage.setItem(SELECTED_KEY, created._id);
            }
            await fetchCampaigns();
        } catch (err: any) {
            setError(err?.message || 'Failed to create campaign');
        } finally {
            setCreating(false);
        }
    };

    const selectCampaign = (id: string | null) => {
        setSelectedCampaignId(id);
        if (typeof window !== 'undefined') {
            if (id) localStorage.setItem(SELECTED_KEY, id);
            else localStorage.removeItem(SELECTED_KEY);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    return {
        campaigns,
        loading,
        error,
        creating,
        newCampaignName,
        setNewCampaignName,
        fetchCampaigns,
        createCampaign,
        selectedCampaignId,
        selectCampaign,
        page,
        totalPages,
        pageSize,
        totalItems,
        setPage,
    };
};

export default useCampaignData